//@flow
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import hash from 'object-hash';
import sr from 'seedrandom';
import moment from 'moment';
import environment from '../../../../environment';
import Reviews from '../Reviews/Reviews';
import {LastDeliveries} from '../LastDeliveries/LastDeliveries';
import Modal from '../../../../elements/Modal/Modal';
import {Redirect} from 'react-router-dom';
import {FormValidator} from '../../../../utils/form';
import Cleave from 'cleave.js/react';
import {Helmet} from 'react-helmet';

import style from './FlightLanding.scss';
import star from '../../../../assets/img/flight/star.svg';
import metaImage from '../../../../assets/img/flight/landing/meta_image.jpeg';
import icons from '../../../../assets/styles/icons.scss';
import formStyle from '../../../../assets/styles/form.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import inputStyle from '../../../../assets/styles/input.scss';

import type {Flight, User} from '../../../../entities';
import type {Location, Match} from '../../../../router';
import type {FormValidationResult} from '../../../../utils/form';

type Props = {
  location: Location;
  match: Match;
  fetchFlight: (id: string) => void;
  flight?: Flight;
  saveData: (id: string, email: string, phone: string, product: string) => void;
  isSavingData?: boolean;
  user?: User;
};

type State = {
  modalOpen: boolean;
  email: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  product: {
    value: string;
    errors?: string[];
  };
};

export default class FlightLanding extends Component<Props, State> {
  urlIdFlight: string;
  urlUser: string;
  flightTravelerIdHash: string;
  randomFromFlightTravelerIdHash: number;
  formValidator: FormValidator;
  state = {
    modalOpen: false,
    email: {
      value: '',
    },
    phone: { 
      value: '', 
    },
    product: {
      value: '',
    },
  };

  constructor(props: Props) {
    super(props);
    if (props.match.params) {
      this.urlIdFlight = props.match.params.id;
      this.urlUser = props.match.params.user;
    }
    this.formValidator = new FormValidator({
      email: {
        required: 'required',
        emailFormat: 'emailFormat',
      },
      phone: {
        required: 'required',
      },
      product: {
        required: 'required',
      },
    });
  }
  
  componentDidMount() {
    const { fetchFlight, flight } = this.props;
    if (!flight) {
      fetchFlight(this.urlIdFlight);
    }
  }
  
  componentDidUpdate(prevProps: Props, prevState: State) {
    const { isSavingData, user } = this.props;
    const { modalOpen } = this.state;
    if (prevProps.isSavingData && !isSavingData) {
      this.setState({ 
        modalOpen: false,
        email: {
          value: '',
        },
        phone: {
          value: '',
        },
        product: {
          value: '',
        },
      });
    }
    
    if (!prevState.modalOpen && modalOpen) {
      if (user) {
        this.setState({
          email: {
            value: user.email,
          },
          phone: {
            value: user.phone,
          },
        });
      }
    }
  }

  checkTravelerName = (flight: Flight, userName: string): boolean => {
    const n = flight.traveler.name.toLowerCase().split(' ').join('');
    if (n !== userName) {
      console.log('Traveler name missmatch!', `${n} === ${userName}`);
      return false;
    }
    return true;
  };

  formatFlightLocation = (flight: Flight): [string, string] => {
    let d, a;
    if (flight.departureAdmin2) {
      d = `${flight.departureAdmin2.name}, ${flight.departureCountry.code3}`;
    } else if (flight.departureAdmin1) {
      d = `${flight.departureAdmin1.name}, ${flight.departureCountry.code3}`;
    } else {
      d = flight.departureCountry.code3;
    }
    if (flight.arrivalAdmin2) {
      a = `${flight.arrivalAdmin2.name}, ${flight.arrivalCountry.code3}`;
    } else if (flight.arrivalAdmin1) {
      a = `${flight.arrivalAdmin1.name}, ${flight.arrivalCountry.code3}`;
    } else {
      a = flight.arrivalCountry.code3;
    }
    return [d, a];
  };
  
  initializeFlightTravelerHash = (flight?: Flight) => {
    if (flight) {
      if (!this.flightTravelerIdHash) {
        this.flightTravelerIdHash = hash(flight.traveler.id);
      }
      if (typeof this.randomFromFlightTravelerIdHash !== 'number') {
        this.randomFromFlightTravelerIdHash = sr(this.flightTravelerIdHash)();
      }
    }
  };
  
  getTravelerSinceYear = (flight: Flight) => {
    this.initializeFlightTravelerHash(flight);
    return 2014 + Math.floor(this.randomFromFlightTravelerIdHash * 6) + 1;
  };
  
  getStarsRating = (flight?: Flight) => {
    this.initializeFlightTravelerHash(flight);
    if (flight && flight.traveler.id === 'U16276495') return 5;
    return 3 + Math.floor(this.randomFromFlightTravelerIdHash * 2) + 1;
  };
  
  getSuccessfulOrders = (flight?: Flight) => {
    this.initializeFlightTravelerHash(flight);
    return 50 + Math.floor(this.randomFromFlightTravelerIdHash * 30) + 1;
  };
  
  getFakeInfoOffset = (flight?: Flight) => {
    this.initializeFlightTravelerHash(flight);
    return Math.floor(this.randomFromFlightTravelerIdHash * 100);
  };

  generateWhatsappLink = (flight: Flight, product: string) => {
    const formatted = this.formatFlightLocation(flight);
    const date = moment(flight.flightDate).utc().format('DD-MM-YYYY', { trim: false });
    return `https://wa.me/${flight.traveler.phone}?text=` +
      `Hola, quiero cotizar la importación del siguiente producto en tu viaje del (${date})` +
      ` de ${formatted[0]} hacia ${formatted[1]}: ${product}`;
  };

  handleSubmit = (e: Event) => {
    const { saveData } = this.props;
    const { email, phone, product } = this.state;
    const errors = this.formValidator.validateForm({
      email: email.value,
      phone: phone.value.split('-').join(''),
      product: product.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
      return e.preventDefault();
    }
    saveData(this.urlIdFlight, email.value, phone.value, product.value);
  };

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleBlur = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      const errors = this.formValidator.validateField(name, value);
      if (errors.length && (errors[0] === 'emailFormat')) {
        this.setState({ [name]: { value, errors } });
      } else {
        this.setState({ [name]: { value } });
      }
    }
  };
  
  render() {
    const { flight, isSavingData } = this.props;
    const { modalOpen, email, phone, product } = this.state;
    
    if (!flight) return null;
    
    if (!this.checkTravelerName(flight, this.urlUser)) 
      return (<Redirect to="/" />);
    
    const starsCount = this.getStarsRating(flight);
    const orders = this.getSuccessfulOrders(flight);
    const offset = this.getFakeInfoOffset(flight);
    
    const stars = (
      <div className={style.starsWrap}>
        {(new Array(starsCount)).fill(0).map((_: any, i: number) =>
          <i key={`fill-${i}`}
             className={classNames(icons.iconStarMedium, style.full)}/>)}
        {(new Array(5 - starsCount)).fill(0).map((_: any, i: number) =>
          <i key={`empty-${i}`}
             className={classNames(icons.iconStarMedium, style.empty)}/>)}
        <span>({orders})</span>
      </div>
    );
    
    const flightLocationFormatted = flight ? this.formatFlightLocation(flight) : undefined;

    const emailError = email.errors && FormValidator.buildErrorLabel(email.errors, {
      emailFormat: 'auth.email.error.format',
      required: 'auth.email.error.required',
    });

    const phoneError = phone.errors && FormValidator.buildErrorLabel(phone.errors, {
      required: 'preferences.security.phone.phone.error.required',
    });

    const productError = product.errors && FormValidator.buildErrorLabel(product.errors, {
      required: 'tiendas.landing.name.error.required',
    });

    const title = flight ? `Trae tus compras de USA con ${flight.traveler.name}` : 'Trae tus compras de USA';
    const date = flight ? moment(flight.flightDate).utc().format('DD-MM-YYYY', { trim: false }) : '';
    const description = date ? `Aprovecha y trae todo lo que necesitas para el ${date}` : 
      'Aprovecha y trae todo lo que necesitas';

    const baseUrl = environment.baseUrl['PE'];
    const coverImg = `${baseUrl}${metaImage}`;
    
    return (
      <Fragment>
        {flight && <Helmet meta={[
          {name: 'description', content: description},
          {property: 'og:image', content: coverImg},
          {property: 'og:title', content: title},
          {property: 'og:description', content: description},
          {name: 'twitter:title', content: title},
          {name: 'twitter:image', content: coverImg},
          {name: 'twitter:description', content: description},
        ]}>
          <title>{title}</title>
        </Helmet>}
        <div className={style.flightLandingWrap}>
          {flight && flight.traveler && (
            <Fragment>
              <div className={style.topWrap}>
                <div className={style.imageWrap}>
                  <img src={flight.traveler.image} alt="Traveler"/>
                </div>
                <div className={style.contentWrap}>
                  <h4>{flight.traveler.name} <span className={style.verified}>
                <img src={star} alt="star"/>
                <i className={icons.symbolCheck}/>
              </span></h4>
                  <p>Soy {flight.traveler.name} y tengo espacio para traer tus compras en mi siguiente viaje.</p>
                  <div className={style.sinceWrap}>
                    <span><i className={icons.iconAirplaneLight} />Viajero Qempo desde {this.getTravelerSinceYear(flight)}</span>
                  </div>
                  {stars}
                </div>
              </div>
              {flightLocationFormatted && (
                <div className={style.travelWrap}>
                  <span><strong>Próximo viaje:</strong> {flightLocationFormatted[0]} - {flightLocationFormatted[1]}</span>
                  <div className={style.vr}/>
                  <span className={style.date}>
                <strong>Fecha:</strong> {moment(flight.flightDate).utc().format('DD MMMM YYYY', { trim: false })}
              </span>
                  <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                          onClick={_ => this.setState({ modalOpen: true })}
                    /*href={this.generateWhatsappLink(flight)}*/>
                    <i className={icons.symbolWhatsapp} />Trae tu compras con {flight.traveler.name}
                  </button>
                </div>
              )}
              <Reviews index={offset} />
              <LastDeliveries index={offset} traveler={flight.traveler.name} />
            </Fragment>
          )}
        </div>
        <Modal open={modalOpen || isSavingData}
               handleClose={() => this.setState({ modalOpen: false })}
               big>
          <form className={classNames(formStyle.formWrap, style.form)}
                onSubmit={(e) => e.preventDefault()}>
            <h3>Datos de contacto</h3>
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <label className={formStyle.label}>E-mail</label>
              <input type="email"
                     value={email.value}
                     className={classNames(inputStyle.input,
                       { [inputStyle.red]: email.errors && email.errors.length },
                       { [inputStyle.lightBlue]: !!email.value })}
                     onChange={this.handleChange}
                     onFocus={this.handleFocus}
                     onBlur={this.handleBlur}
                     disabled={isSavingData}
                     name="email"
                     placeholder="Correo"/>
              {emailError}
            </section>
            <section className={classNames(formStyle.row, formStyle.inputRow, style.phoneWrap)}>
              <label className={formStyle.label}>Whatsapp</label>
              <label className={classNames(style.prefix, { [style.error]: phone.errors && phone.errors.length })}>+51</label>
              <Cleave value={phone.value || ''}
                      options={{
                        phone: true,
                        phoneRegionCode: 'PE',
                        delimiter: '-',
                      }}
                      className={classNames(inputStyle.input, { [inputStyle.red]: phone.errors && phone.errors.length },
                        { [inputStyle.lightBlue]: !!phone.value }, style.phoneInput)}
                      onChange={this.handleChange}
                      disabled={isSavingData}
                      name="phone"/>
              {phoneError}
            </section>
            <section className={classNames(formStyle.row, formStyle.inputRow)}>
              <label className={formStyle.label}>¿Qué deseas comprar?</label>
              <input type="text"
                     value={product.value}
                     className={classNames(inputStyle.input,
                       { [inputStyle.red]: product.errors && product.errors.length },
                       { [inputStyle.lightBlue]: !!product.value })}
                     onChange={this.handleChange}
                     onFocus={this.handleFocus}
                     onBlur={this.handleBlur}
                     disabled={isSavingData}
                     name="product"
                     placeholder="Producto"/>
              {productError}
            </section>
            <a className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
               href={this.generateWhatsappLink(flight, product.value)}
               target="_blank"
               onClick={e => this.handleSubmit(e)}>
              <i className={icons.symbolWhatsapp} />Contactar a {flight.traveler.name}
            </a>
          </form>
        </Modal>
      </Fragment>
    );
  }
}

FlightLanding.propTypes = {
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  fetchFlight: PropTypes.func.isRequired,
  flight: PropTypes.object,
  saveData: PropTypes.func.isRequired,
  isSavingData: PropTypes.bool,
  user: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/modules/flight-landing/components/FlightLanding/FlightLanding.jsx